import React, { ReactNode, useContext, useEffect, useState } from 'react'
import { useRequest } from 'ahooks'
import { join } from 'lodash'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import MaskedInput from 'react-maskedinput'
import styled from 'styled-components'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import { STATIC_DATA_STATES, UnitsTypes } from '../chart-components'
import { ModalField, CustomMeasureSelect, CustomDatePicker, ModalSectionHeader, NumberInput } from '../modal-components'
import { timestampToDateTime, dateAndTimeRegex, dateTimeToTimestamp, regexNumberAndComma } from '../../utils/parse'
import { fetchEntityOptions, fetchSingleEntity, fetchUnitOptions, RUN_API, SECTION_API } from '../../api/api'
import { fetchCasingValues, fetchIsWellOnshore } from '../../api/phaseApi'
import { RawPhaseEvent, SelectOptions, Uom } from '../../utils/type'
import { PhaseErrors } from './types'
import { StaticDataContext } from '../../context/StaticDataContext'
interface PhaseTabProps {
    event: RawPhaseEvent
    children?: ReactNode
    errors: PhaseErrors
    updatePhaseEvent: (event: RawPhaseEvent) => void
    noPreviousCasing: boolean
    setNoPreviousCasing: (value: boolean) => void
    setWellOnShore: (value: boolean) => void
    hasPermissionToEdit: boolean
}

const PhaseTab: React.FC<PhaseTabProps> = ({
    event,
    errors,
    updatePhaseEvent,
    setNoPreviousCasing,
    noPreviousCasing,
    setWellOnShore,
    hasPermissionToEdit
}: PhaseTabProps) => {
    const [selectedEvent, setSelectedEvent] = useState<RawPhaseEvent>(event)
    const eventStartDateTime = timestampToDateTime(event?.start)
    const eventEndDateTime = timestampToDateTime(event?.end)
    const [startDate, setStartDate] = useState(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState(eventEndDateTime?.time || null)
    const [unitOptions, setUnitOptions] = useState<Uom[]>([])
    const [entityOptions, setEntityOptions] = useState<{ type: SelectOptions[]; riser: SelectOptions[] } | null>(null)
    const { liveTheme } = useContext(StaticDataContext)

    const fieldDisabledAfterEntityStart = selectedEvent.state !== STATIC_DATA_STATES.EMPTY

    const { data: casingValues } = useRequest(() => fetchCasingValues(event.intervention_id, event.start))
    const { data: resultIsWellOnShore } = useRequest(() => fetchIsWellOnshore(event.intervention_id))

    const defaultUnit = unitOptions.find(elem => elem.name == 'm') || unitOptions[0]
    const inchesDefaultUnit = unitOptions.find(elem => elem.name == 'in') || defaultUnit

    useEffect(() => {
        fetchUnitOptions(UnitsTypes.LENGTH).then(u => setUnitOptions(u?.options))
        fetchEntityOptions(SECTION_API).then(e => setEntityOptions(e))
    }, [])

    useEffect(() => {
        event.state === STATIC_DATA_STATES.EMPTY &&
            !noPreviousCasing &&
            !event.id &&
            setSelectedEvent(prevstate => ({
                ...prevstate,
                prevCasingOD: {
                    value: casingValues?.prevCasingOD?.value,
                    uom: casingValues?.prevCasingOD?.uom ?? inchesDefaultUnit
                },
                prevCasingFinalMD: {
                    value: casingValues?.prevCasingFinalMD?.value,
                    uom: casingValues?.prevCasingFinalMD?.uom ?? defaultUnit
                }
            }))
    }, [casingValues, event.state, event.id])

    useEffect(() => {
        if (unitOptions) {
            setSelectedEvent(prevstate => ({
                ...prevstate,
                oD: {
                    value: prevstate?.oD?.value,
                    uom: prevstate?.oD?.uom || inchesDefaultUnit
                },
                startMD: {
                    value: prevstate?.startMD?.value,
                    uom: prevstate?.startMD?.uom || defaultUnit
                },
                finalMD: {
                    value: prevstate?.finalMD?.value,
                    uom: prevstate?.finalMD?.uom || defaultUnit
                }
            }))
        }
    }, [unitOptions])

    useEffect(() => {
        if (resultIsWellOnShore) setWellOnShore(true)
    }, [resultIsWellOnShore])

    useEffect(() => {
        updatePhaseEvent(selectedEvent)
    }, [selectedEvent])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([startDate, startHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([startDate, startHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, start: timestamp }))
        }
    }, [startDate, startHour])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([endDate, endHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([endDate, endHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, end: timestamp }))
        }
    }, [endDate, endHour])

    useEffect(() => {
        if (eventStartDateTime?.date !== startDate || eventStartDateTime?.time !== startHour) {
            setStartDate(eventStartDateTime?.date)
            setStartHour(eventStartDateTime?.time)
        }
    }, [eventStartDateTime?.date, eventStartDateTime?.time])

    useEffect(() => {
        const runs = event?.runs?.sort((a, b) => b.id - a.id)
        if (runs && runs.length > 0) {
            fetchSingleEntity(RUN_API, runs[0].id).then(fetched => {
                if (fetched?.end) {
                    const fetchedEnd = timestampToDateTime(fetched.end)
                    setEndDate(fetchedEnd.date)
                    setEndHour(fetchedEnd.time)
                }
            })
        }
    }, [event?.runs])

    const handleNoPreviousCasingCheckbox = () => {
        const newValue = !noPreviousCasing
        setNoPreviousCasing(newValue)
        setSelectedEvent(prevstate => ({
            ...prevstate,
            prevCasingOD: newValue
                ? null
                : {
                      value: casingValues?.prevCasingOD?.value ?? null,
                      uom: casingValues?.prevCasingOD?.uom || inchesDefaultUnit
                  },
            prevCasingFinalMD: newValue
                ? null
                : {
                      value: casingValues?.prevCasingFinalMD?.value ?? null,
                      uom: casingValues?.prevCasingFinalMD?.uom || defaultUnit
                  }
        }))
    }

    return (
        <>
            <div onMouseDown={e => e.stopPropagation()}>
                <ModalSectionHeader label={i18n('Attributes')} />
                <ModalField
                    label={'Name'}
                    field={
                        <input
                            disabled={!hasPermissionToEdit}
                            placeholder={i18n('Section name')}
                            type="text"
                            value={selectedEvent.name || ''}
                            onChange={e => {
                                e.persist()
                                setSelectedEvent(prevstate => ({ ...prevstate, name: e?.target?.value }))
                            }}
                        />
                    }
                />
                <ModalField
                    label={`OD`}
                    labelAditionalField={
                        <CustomMeasureWithTooltip>
                            <CustomMeasureSelect
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        oD: uomValue
                                    }))
                                }
                                selectedOptionValues={selectedEvent.oD}
                                options={unitOptions}
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={''}>
                                        {i18n(
                                            'You must fill this field with a value less than the OD value from the previous section.'
                                        )}
                                    </Tooltip>
                                }
                            >
                                <i
                                    style={{
                                        color: theme['link-color'],
                                        cursor: 'default',
                                        marginLeft: '8px',
                                        paddingBottom: '2px'
                                    }}
                                    className="far fa-info-circle"
                                />
                            </OverlayTrigger>
                        </CustomMeasureWithTooltip>
                    }
                    field={
                        <NumberInput
                            disabled={!hasPermissionToEdit}
                            value={selectedEvent.oD.value ?? ''}
                            onChange={e => {
                                if (regexNumberAndComma.test(e?.target?.value)) {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        oD: {
                                            value: Number(e?.target?.value),
                                            uom: selectedEvent.oD.uom
                                        }
                                    }))
                                }
                                if (e?.target?.value === '') {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        oD: {
                                            uom: selectedEvent.oD.uom,
                                            value: null
                                        }
                                    }))
                                }
                            }}
                            style={errors?.oD ? { borderColor: '#D9534F' } : {}}
                            placeholder={`0.00`}
                        />
                    }
                />
                <FlexContainer>
                    <ModalField
                        label={'Start MD'}
                        labelAditionalField={
                            <CustomMeasureWithTooltip>
                                <CustomMeasureSelect
                                    disabled
                                    setSelectedUomValue={uomValue =>
                                        setSelectedEvent(prevstate => ({ ...prevstate, startMD: uomValue }))
                                    }
                                    selectedOptionValues={selectedEvent.startMD}
                                    options={unitOptions}
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip style={{ zIndex: 9999 }} id={''}>
                                            {i18n(
                                                'You must fill this field with a value less than the Final MD value from the previous section.'
                                            )}
                                        </Tooltip>
                                    }
                                >
                                    <i
                                        style={{
                                            color: theme['link-color'],
                                            cursor: 'default',
                                            marginLeft: '8px',
                                            paddingBottom: '2px'
                                        }}
                                        className="far fa-info-circle"
                                    />
                                </OverlayTrigger>
                            </CustomMeasureWithTooltip>
                        }
                        field={
                            <NumberInput
                                disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                value={selectedEvent.startMD?.value ?? ''}
                                onChange={e => {
                                    if (regexNumberAndComma.test(e?.target?.value)) {
                                        e.persist()
                                        setSelectedEvent(prevstate => ({
                                            ...prevstate,
                                            startMD: {
                                                value: Number(e?.target?.value),
                                                uom: selectedEvent.startMD.uom
                                            }
                                        }))
                                    }
                                    if (e?.target?.value === '') {
                                        e.persist()
                                        setSelectedEvent(prevstate => ({
                                            ...prevstate,
                                            startMD: {
                                                uom: selectedEvent.startMD.uom,
                                                value: null
                                            }
                                        }))
                                    }
                                }}
                                placeholder={`0.00`}
                            />
                        }
                    />
                    <ModalField
                        label={'Final MD'}
                        labelAditionalField={
                            <CustomMeasureSelect
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, finalMD: uomValue }))
                                }
                                selectedOptionValues={selectedEvent.finalMD}
                                options={unitOptions}
                            />
                        }
                        field={
                            <NumberInput
                                disabled={!hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED}
                                value={selectedEvent.finalMD?.value ?? ''}
                                onChange={e => {
                                    if (regexNumberAndComma.test(e?.target?.value)) {
                                        e.persist()
                                        setSelectedEvent(prevstate => ({
                                            ...prevstate,
                                            finalMD: {
                                                uom: selectedEvent.finalMD.uom,
                                                value: Number(e?.target?.value)
                                            }
                                        }))
                                    }
                                    if (e?.target?.value === '') {
                                        e.persist()
                                        setSelectedEvent(prevstate => ({
                                            ...prevstate,
                                            finalMD: {
                                                uom: selectedEvent.finalMD.uom,
                                                value: null
                                            }
                                        }))
                                    }
                                }}
                                placeholder={`0.00`}
                            />
                        }
                    />
                </FlexContainer>
                <ModalField
                    field={
                        <div className="input-date-time">
                            <ModalField
                                label={'Start Date'}
                                field={
                                    <CustomDatePicker
                                        isDisabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                        date={startDate}
                                        setDate={setStartDate}
                                    />
                                }
                            />
                            <ModalField
                                label={'Start Hour'}
                                field={
                                    <MaskedInput
                                        disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                        style={errors?.start ? { borderColor: '#D9534F' } : {}}
                                        mask="11:11"
                                        type="text"
                                        placeholder={'00:00'}
                                        value={startHour}
                                        onChange={e => setStartHour(e.target.value)}
                                    />
                                }
                            />
                        </div>
                    }
                />
                {selectedEvent.state != STATIC_DATA_STATES.EMPTY && (
                    <ModalField
                        field={
                            <div className="input-date-time">
                                <ModalField
                                    label={'End Date'}
                                    field={
                                        <CustomDatePicker
                                            isDisabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            date={endDate}
                                            setDate={setEndDate}
                                        />
                                    }
                                />
                                <ModalField
                                    label={'End Hour'}
                                    field={
                                        <MaskedInput
                                            disabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            mask="11:11"
                                            type="text"
                                            placeholder={'00:00'}
                                            value={endHour}
                                            onChange={e => setEndHour(e.target.value)}
                                        />
                                    }
                                />
                            </div>
                        }
                    />
                )}
                <ModalField
                    field={
                        <div className="input-checkbox">
                            <input
                                type="checkbox"
                                checked={noPreviousCasing}
                                disabled={!hasPermissionToEdit}
                                onChange={handleNoPreviousCasingCheckbox}
                            />
                            <span className="no-previous-casing-checkbox">{i18n('No Previous Casing')}</span>
                        </div>
                    }
                />
                <ModalField
                    label={`Prev Casing OD`}
                    labelAditionalField={
                        <CustomMeasureWithTooltip>
                            <CustomMeasureSelect
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, prevCasingOD: uomValue }))
                                }
                                selectedOptionValues={selectedEvent.prevCasingOD}
                                options={unitOptions}
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={''}>
                                        {i18n(
                                            'This field is automatically filled with the Prev Casing OD from the previous section.'
                                        )}
                                    </Tooltip>
                                }
                            >
                                <i
                                    style={{
                                        color: theme['link-color'],
                                        cursor: 'default',
                                        marginLeft: '8px',
                                        paddingBottom: '2px'
                                    }}
                                    className="far fa-info-circle"
                                />
                            </OverlayTrigger>
                        </CustomMeasureWithTooltip>
                    }
                    field={
                        <NumberInput
                            disabled={!hasPermissionToEdit || noPreviousCasing}
                            value={selectedEvent.prevCasingOD?.value ?? ''}
                            onChange={e => {
                                if (regexNumberAndComma.test(e?.target?.value)) {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        prevCasingOD: {
                                            uom: selectedEvent.prevCasingOD?.uom,
                                            value: Number(e?.target?.value)
                                        }
                                    }))
                                }
                                if (e?.target?.value === '') {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        prevCasingOD: {
                                            uom: selectedEvent.prevCasingOD?.uom,
                                            value: null
                                        }
                                    }))
                                }
                            }}
                            placeholder={noPreviousCasing ? '-' : '0.00'}
                            style={errors?.prevCasingOD ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label={'Prev Casing Final MD'}
                    labelAditionalField={
                        <CustomMeasureWithTooltip>
                            <CustomMeasureSelect
                                disabled
                                setSelectedUomValue={uomValue =>
                                    setSelectedEvent(prevstate => ({ ...prevstate, prevCasingFinalMD: uomValue }))
                                }
                                selectedOptionValues={selectedEvent.prevCasingFinalMD}
                                options={unitOptions}
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip style={{ zIndex: 9999 }} id={''}>
                                        {i18n(
                                            'This field is automatically filled with the Prev Casing Final MD from the previous section.'
                                        )}
                                    </Tooltip>
                                }
                            >
                                <i
                                    style={{
                                        color: theme['link-color'],
                                        cursor: 'default',
                                        marginLeft: '8px',
                                        paddingBottom: '2px'
                                    }}
                                    className="far fa-info-circle"
                                />
                            </OverlayTrigger>
                        </CustomMeasureWithTooltip>
                    }
                    field={
                        <NumberInput
                            disabled={!hasPermissionToEdit || noPreviousCasing}
                            value={selectedEvent.prevCasingFinalMD?.value ?? ''}
                            onChange={e => {
                                if (regexNumberAndComma.test(e?.target?.value)) {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        prevCasingFinalMD: {
                                            uom: selectedEvent.prevCasingFinalMD?.uom,
                                            value: Number(e?.target?.value)
                                        }
                                    }))
                                }
                                if (e?.target?.value === '') {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        prevCasingFinalMD: {
                                            uom: selectedEvent.prevCasingFinalMD?.uom,
                                            value: null
                                        }
                                    }))
                                }
                            }}
                            placeholder={noPreviousCasing ? '-' : '0.00'}
                            style={errors?.prevCasingFinalMD ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label={'Type'}
                    field={
                        <Select
                            disabled={!hasPermissionToEdit}
                            searchable={false}
                            clearable={false}
                            options={entityOptions?.type}
                            placeholder={selectedEvent.type ? `${selectedEvent.type}` : i18n('Select...')}
                            value={selectedEvent.type !== '' && selectedEvent.type}
                            onChange={value => setSelectedEvent(prevstate => ({ ...prevstate, type: value }))}
                        />
                    }
                />
                <ModalField
                    label={'Riser'}
                    field={
                        <Select
                            disabled={!hasPermissionToEdit || resultIsWellOnShore}
                            style={errors?.riser ? { borderColor: '#D9534F' } : {}}
                            searchable={false}
                            clearable={false}
                            options={entityOptions?.riser}
                            placeholder={selectedEvent.riser ? `${selectedEvent.riser}` : i18n('Select...')}
                            value={selectedEvent.riser !== '' && selectedEvent.riser}
                            onChange={value => setSelectedEvent(prevstate => ({ ...prevstate, riser: value }))}
                        />
                    }
                />
            </div>
        </>
    )
}
const CustomMeasureWithTooltip = styled.div`
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
`

const FlexContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`

export default PhaseTab
